'use client';
import React, { FC, useEffect, useState } from 'react';
import { LuvImage } from '@/components/ui/LuvImage';
import { getMediaComponent } from '@/components/common/LandingPageHero/utils';
import { RichText } from '@/components';
import { Document } from '@contentful/rich-text-types';
import { ExtendedTypeHomepageHeroFields } from '@/components/common/HomepageHero/HomepageHero';
import classNames from 'classnames';
import styles from '../HomepageHero.module.scss';
import { UniversalCta } from '../../UniversalCta';
import useAuth from '@/context/AuthContext';

export const SideBySide: FC<ExtendedTypeHomepageHeroFields> = ({
  entryName,
  mobileMediaIsVideo,
  altText,
  typeImage,
  desktopMedia,
  mobileMedia,
  whiteTextDesktop,
  whiteTextMobile,
  subtext = '',
  headerText = '',
  cta,
  secondaryCTA,
  desktopCtaAlignment,
  loggedOutText,
  loggedInText,
}) => {
  const [hydrated, setHydrated] = useState(false);
  const { user } = useAuth();
  const useLoggedInText = !!user && loggedInText;
  const textToDisplay = useLoggedInText ? loggedInText : loggedOutText;

  // This hydrated state is needed to avoid hydration errors due to SSR https://nextjs.org/docs/messages/react-hydration-error#solution-1-using-useeffect-to-run-on-the-client-only
  useEffect(() => {
    setHydrated(true);
  }, []);

  return (
    <section
      data-testid={`homepage-hero-${entryName}`}
      className="mx-auto text-center lg:text-left"
    >
      <div className="lg:flex">
        <div className="mb-6 hidden h-[240px] w-full items-center justify-center lg:mb-0 lg:block lg:h-[465px] lg:w-1/2 2xl:h-[656px]">
          {getMediaComponent({
            media: desktopMedia,
            classes: 'bg-cover bg-center h-full',
          })}
        </div>
        <div className="block lg:hidden">
          {mobileMediaIsVideo ? (
            getMediaComponent({
              media: mobileMedia,
              classes: '',
            })
          ) : (
            <LuvImage
              imageData={typeImage}
              alt={altText}
              height={648}
              width={750}
              loading="eager"
            />
          )}
        </div>

        <div className="items-center justify-start px-6 lg:flex lg:w-1/2 lg:pl-12 lg:pr-12 xl:pl-24">
          <div
            className={classNames(
              'py-6 md:max-w-none',
              whiteTextDesktop ? 'lg:text-white' : '',
              whiteTextMobile ? 'text-white' : '',
            )}
          >
            <div className="mx-auto max-w-[328px]">
              {subtext && (
                <h4 className="mb-4 text-d-base font-bold lg:text-lg 2xl:mb-6 2xl:text-d-h3">
                  {subtext}
                </h4>
              )}
              <h1 className="mb-4 text-d-h2 font-bold lg:text-d-h2 2xl:mb-8 2xl:text-d-hero">
                {headerText}
              </h1>
            </div>
            <div
              className={classNames(
                'flex flex-col lg:inline-flex',
                desktopCtaAlignment === 'row' ? 'gap-4 lg:flex-row' : 'gap-3',
                secondaryCTA && desktopCtaAlignment === 'row'
                  ? 'lg:w-[95%]'
                  : '',
              )}
            >
              {cta && (
                <UniversalCta
                  link={cta}
                  className={secondaryCTA ? 'lg:grow lg:basis-0' : ''}
                />
              )}
              {secondaryCTA && (
                <UniversalCta
                  link={secondaryCTA}
                  defaultButtonStyle="Secondary"
                  className="lg:grow lg:basis-0"
                />
              )}
            </div>
            {textToDisplay && hydrated && (
              <div
                className={classNames(
                  `mt-5 ${styles.homepageHero}`,
                  whiteTextDesktop ? 'lg:text-white' : '',
                  whiteTextMobile ? 'text-white' : '',
                  desktopCtaAlignment === 'row' ? 'lg:mt-7' : 'lg:mt-3',
                )}
              >
                <RichText
                  content={textToDisplay as Document}
                  addParagraphMargins={false}
                  openLinkSameWindow={true}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
